/**
 * invoiceApi — Ausgangsrechnungen über atomare DB-Funktionen.
 *
 * Nummer, Beleg, Buchung und offener Posten entstehen in EINER Transaktion
 * (supabase/migrations/20261016_02_fibu_rechnungen_rpc.sql). Beträge rechnet
 * die Datenbank — identisch zu lib/invoiceCalc.js.
 */
import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';

export const COMPANY_ID = 1;

function client() {
  const c = SupabaseAdapter.getClient();
  if (!c) throw new Error('Keine Verbindung zur Datenbank.');
  return c;
}

/** RPC fehlt (Migration noch nicht ausgeführt)? */
export function isMissingRpc(error) {
  return error?.code === 'PGRST202' || /Could not find the function/i.test(error?.message || '');
}

async function rpc(name, args) {
  const { data, error } = await client().rpc(name, args);
  if (error) {
    const err = new Error(error.code === 'P0001' ? error.message : (error.message || 'Vorgang fehlgeschlagen.'));
    err.code = isMissingRpc(error) ? 'RPC_FEHLT' : error.code;
    err.hint = error.hint;
    throw err;
  }
  return data;
}

/** Rechnung zu einem Auftrag erstellen bzw. vorhandene zurückgeben (idempotent). */
export const issueInvoiceFromOrder = (orderId, actor) =>
  rpc('fibu_invoice_issue_from_order', { p_order_id: orderId, p_actor: actor || null });

export const createManualInvoice = (payload, actor) =>
  rpc('fibu_invoice_create_manual', { p_payload: payload, p_actor: actor || null });

export const finalizeInvoice = (invoiceId, actor) =>
  rpc('fibu_invoice_finalize', { p_invoice_id: invoiceId, p_actor: actor || null });

export const deleteDraftInvoice = (invoiceId) =>
  rpc('fibu_invoice_delete_draft', { p_invoice_id: invoiceId });

export const cancelInvoice = (invoiceId, reason, actor) =>
  rpc('fibu_invoice_cancel', { p_invoice_id: invoiceId, p_reason: reason, p_actor: actor || null });

/**
 * @param {number} invoiceId
 * @param {Array<{pos:number, quantity:number}|{net:number, tax_rate:number, description?:string}>} items
 */
export const createGutschrift = (invoiceId, items, reason, actor) =>
  rpc('fibu_gutschrift_create', { p_invoice_id: invoiceId, p_items: items, p_reason: reason || null, p_actor: actor || null });

export const recordPaymentRpc = ({ openItemId, amount, paymentDate, bankAccountNo = '1800', actorEmail, note, transactionId }) =>
  rpc('fibu_record_payment', {
    p_open_item_id: openItemId,
    p_amount: Number(amount),
    p_date: paymentDate,
    p_account_no: bankAccountNo,
    p_actor: actorEmail || null,
    p_note: note || null,
    p_transaction_id: transactionId || null,
  });

// ── Lesen ──────────────────────────────────────────────────────────────

export async function listInvoices() {
  const { data, error } = await client().from('fibu_invoices')
    .select('*').eq('company_id', COMPANY_ID).order('id', { ascending: false });
  if (error) {
    if (/fibu_invoices/.test(error.message || '') && /exist|find/i.test(error.message || '')) return null;
    throw error;
  }
  return data || [];
}

export async function getInvoiceDetail(invoiceId) {
  const c = client();
  const [{ data: inv, error }, { data: lines }, ] = await Promise.all([
    c.from('fibu_invoices').select('*').eq('id', invoiceId).maybeSingle(),
    c.from('fibu_invoice_lines').select('*').eq('invoice_id', invoiceId).order('pos'),
  ]);
  if (error) throw error;
  if (!inv) return null;
  let payments = [];
  if (inv.open_item_id) {
    const { data } = await c.from('fibu_open_item_payments')
      .select('*').eq('open_item_id', inv.open_item_id).order('payment_date');
    payments = data || [];
  }
  let storno = null;
  if (inv.status === 'cancelled') {
    const { data } = await c.from('fibu_invoices').select('id, invoice_no, invoice_date, notes')
      .eq('storno_of', inv.id).maybeSingle();
    storno = data || null;
  }
  return { ...inv, lines: lines || [], payments, storno };
}

/** Rechnung zu einer Rechnungsnummer (für PDF mit gebuchten Werten) */
export async function findInvoiceByNo(invoiceNo) {
  if (!invoiceNo) return null;
  try {
    const { data } = await client().from('fibu_invoices')
      .select('*').eq('company_id', COMPANY_ID).eq('invoice_no', invoiceNo).eq('kind', 'invoice').maybeSingle();
    return data || null;
  } catch {
    return null; // z. B. Lager ohne Lesezugriff auf die Buchhaltung
  }
}

export async function listMigrationIssues() {
  const { data, error } = await client().from('fibu_migration_issues')
    .select('*').is('resolved_at', null).order('id');
  if (error) return [];
  return data || [];
}

/**
 * Rechnung (z. B. manuell, ohne Auftrag) in die Auftragsform bringen, die
 * das PDF erwartet. Steuersatz je Position bleibt erhalten.
 */
export function invoiceAsOrder(inv, lines = []) {
  return {
    id: inv.order_id || null,
    order_number: '',
    invoice_number: inv.invoice_no,
    invoiced_at: inv.invoice_date,
    lead_id: inv.lead_id,
    lead_name: inv.customer_name,
    lead_address: inv.customer_address,
    lead_email: inv.customer_email,
    tax_rate: Number(inv.tax_breakdown?.[0]?.tax_rate ?? 19),
    positionen: lines.map((l) => ({
      product_name: l.description,
      product_sku: l.sku,
      quantity: Number(l.quantity),
      unit: l.unit,
      unit_price: Number(l.unit_price_net),
      rabatt_prozent: Number(l.discount_pct) || 0,
      tax_rate: Number(l.tax_rate),
    })),
    _invoice: inv,
  };
}

export const INVOICE_STATUS = {
  draft: { label: 'Entwurf', tone: 'draft' },
  open: { label: 'Offen', tone: 'open' },
  partial: { label: 'Teilbezahlt', tone: 'open' },
  paid: { label: 'Bezahlt', tone: 'paid' },
  cancelled: { label: 'Storniert', tone: 'muted' },
};
